import { makeStyles } from '@material-ui/core';
import React, { useContext } from 'react';
import { useAsync } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CountryPermission, MaterialGroupResponse, useMurexinPimContextApi } from '../../../api';
import { AuthorizationContext } from '../../../auth';
import { NotificationContext } from '../../../contexts';
import { IRouterParams } from '../../../routes';
import PIMFormikSelect from '../../../shared/Components/PIMFormikSelect';
import PIMFormikSidebar from '../../../shared/Components/PIMFormikSidebar';
import PIMFormikTextField from '../../../shared/Components/PIMFormikTextField';

interface ISidebarProps {
  materialGroup?: MaterialGroupResponse;
  handleOnCancel?: () => void;
  handleOnClose?: () => void;
}

export function VariantsSidebar(props: ISidebarProps): JSX.Element {
  const classes = useStyles();
  const { country, productId } = useParams<IRouterParams>();
  const { t } = useTranslation();
  const api = useMurexinPimContextApi();
  const notification = useContext(NotificationContext);
  const { handleOnCancel, handleOnClose, materialGroup } = props;

  const { hasCountryPermission } = useContext(AuthorizationContext);
  const canEditMaterialGroup =
    materialGroup?.countryCode === country && hasCountryPermission(country, CountryPermission.UpdateMaterialGroup);
  const canEditMaterialGroupTitle = hasCountryPermission(country, CountryPermission.Write) || canEditMaterialGroup;

  const variants = useAsync(async () => {
    const response = await api.materialGroupService.materialGroupServiceVariantsDetail(country);
    return response.data ?? [];
  }, [country]);

  const variantOptions =
    variants?.result?.map((variant) => ({
      key: variant.code,
      value: variant.name,
    })) ?? [];

  const onHandleOnSubmit = async (values: any) => {
    await updateMaterialGroupVariant(values);
  };

  interface VariantSidebarModel {
    materialGroupId: number;
    materialGroupDescription: string;
    materialGroupName: string;
    variantCode?: string;
  }

  let sidebarFormikInitialValue: VariantSidebarModel = {
    materialGroupId: 0,
    materialGroupDescription: '',
    materialGroupName: '',
    variantCode: undefined,
  };

  if (materialGroup !== undefined) {
    sidebarFormikInitialValue = {
      materialGroupId: materialGroup.id ?? 0,
      materialGroupDescription: materialGroup.materialGroupDescription ?? '',
      materialGroupName: materialGroup.materialGroupName ?? '',
      variantCode: materialGroup.variantCode ?? '',
    };
  }

  const updateMaterialGroupVariant = async (values: VariantSidebarModel) => {
    if (country && materialGroup?.id && values) {
      try {
        if (canEditMaterialGroup) {
          await api.materialGroupService.materialGroupServiceVariantUpdate(country, values.materialGroupId, {
            variantCode: values.variantCode,
          });
        }

        notification.success(t('Warengruppe erfolgreich aktualisiert'));
        if (handleOnClose) handleOnClose();
      } catch {
        notification.warning(t('Speichern fehlgeschlagen'));
      }
    }
  };

  return (
    <PIMFormikSidebar
      title={t('Variante der Warengruppe bearbeiten')}
      cancelButtonText={t('Abbrechen')}
      formikInitialValues={sidebarFormikInitialValue}
      handleOnSubmit={onHandleOnSubmit}
      handleOnCancel={handleOnCancel}>
      <>
        <PIMFormikTextField
          name='materialGroupName'
          id='filled-basic'
          label={t('Warengruppencode')}
          variant='filled'
          type='text'
          fullWidth
          className={classes.MaterialGroupNumber}
          disabled
        />
        <PIMFormikTextField
          name='materialGroupDescription'
          id='filled-basic'
          label={t('Warengruppenbezeichnung')}
          variant='filled'
          type='text'
          fullWidth
          disabled
        />
        <PIMFormikSelect
          name='variantCode'
          label={t('Variante')}
          options={variantOptions}
          formControlProps={{ fullWidth: true }}
          disabled={!canEditMaterialGroupTitle}
        />
      </>
    </PIMFormikSidebar>
  );
}

const useStyles = makeStyles(() => ({
  separatorLine: {
    margin: '32px 0',
  },
  MaterialGroupNumber: {
    '& .MuiInputAdornment-root': {
      marginRight: '4px',
      color: 'black',
    },
  },
}));
